/**
 * GFG-194 — every user-facing string, label and filename rule for
 * `components/contacts/CampaignContactsModal.tsx`. The modal itself holds no copy literal, so a
 * second screen adopting it (Campaigns, Send Queue, anywhere a campaign's recipients are shown)
 * gets the same wording for free and a copy change lands in exactly one file.
 *
 * Wording is the prototype's (local/tasks/GFG-194/attachments/gfg-admin-dashboard.html —
 * `renderContactsTable`, line ~10983) rather than newly written, so the shipped modal reads
 * exactly like the approved design.
 */

/**
 * Value of the Status select's first option. A sentinel rather than `""` so the select's value is
 * always a meaningful string, and deliberately not a `ContactStatus` — it is the *absence* of a
 * status filter.
 */
export const ALL_STATUSES = "all";

/** Fallback filename stem when a campaign name slugifies to nothing (emoji-only, punctuation). */
const CSV_FALLBACK_SLUG = "campaign";

/** The country a new recipient row starts with. */
export const DEFAULT_RECIPIENT_COUNTRY = "US";

/**
 * Copy used in more than one place; one wording, one place. NAME_REQUIRED and INVALID_EMAIL are
 * shared by the toast and the recipient form; GIFT_REDEEMED is the remove refusal only.
 */
const NAME_REQUIRED = "Enter a first name";
const INVALID_EMAIL = "Enter a valid email address";
const GIFT_REDEEMED = "Gift has been redeemed";

/** All user-facing copy for the campaign-contacts modal. */
export const COPY = {
  title: "Campaign Contacts",
  /** aria-label of the header's icon-only dismiss control. */
  closeLabel: "Close",
  /** Footer text button. */
  close: "Close",
  exportCsv: "Export CSV",
  /** aria-label for the Export button — names what is exported, which "Export CSV" alone doesn't. */
  exportCsvLabel: "Export the contacts currently shown as CSV",
  searchPlaceholder: "Search by name or email",
  searchLabel: "Search contacts",
  statusLabel: "Status",
  allStatuses: "All statuses",
  /**
   * The subtitle under the title. Two forms, matching `renderContactsTable`: the plain recipient
   * count when nothing is filtered, and a "shown of total" line the moment a search term or a
   * status is applied — so the number under the title always describes the rows on screen.
   */
  subtitle: {
    all: (campaign: string, total: number) =>
      `${campaign} · ${total.toLocaleString()} recipient${total === 1 ? "" : "s"}`,
    filtered: (campaign: string, shown: number, total: number, status: string | null) =>
      `${campaign} · ${shown.toLocaleString()} of ${total.toLocaleString()}${
        status ? ` ${status.toLowerCase()}` : ""
      }`,
  },
  /** Table column headers. Also the CSV header row — one label per column, one place. */
  columns: {
    name: "Name",
    email: "Email",
    status: "Status",
    delivered: "Delivered",
    sendDate: "Send Date",
    url: "Redemption Link",
    /**
     * The per-row actions column. The prototype's header cell is empty with an `aria-label`; here
     * the same word is rendered visually hidden, so the column is named for a screen reader
     * without adding a heading the design doesn't have.
     */
    actions: "Actions",
  },
  /**
   * Table-only headers for the two name cells. `columns.name` stays one "Name" because it is also
   * the CSV header (one Name column in the export) and the admin screens copy it.
   */
  columnHeaders: { firstName: "First Name", lastName: "Last Name" },
  /** Cell placeholder for a value the row doesn't have. */
  emptyValue: "—",
  /**
   * The per-row copy control beside the redemption link (prototype `.cmp-copy-btn` →
   * `cmpCopyText(event, c.url, 'Campaign URL')`). The aria-label names the recipient because the
   * table repeats this button on every row and "Copy redemption link" alone would read identically
   * three hundred times in a screen reader's control list.
   *
   * The prototype called this column "Campaign URL"; review renamed it to "Redemption Link"
   * (2026-08-27), and these three strings name the SAME object as the column header, so they move
   * with it — a "Copy campaign URL" tooltip under a "REDEMPTION LINK" heading reads as two things.
   */
  copyUrl: "Copy redemption link",
  copyUrlLabel: (name: string) => `Copy redemption link for ${name}`,
  /** Confirmation after a successful copy — the prototype's toast. */
  copyUrlSuccess: "Redemption link copied to clipboard",
  /**
   * Shown when the clipboard write is refused. `navigator.clipboard` is unavailable on an insecure
   * origin and can be blocked by permissions policy, and silently doing nothing would leave an
   * admin clicking a dead button. The URL is fully readable in the cell's tooltip, so the message
   * points there rather than apologising.
   */
  copyUrlFailure:
    "Couldn't copy — your browser blocked clipboard access. Select the URL to copy it.",
  /** Dismisses the copy confirmation. */
  copyToastClose: "Dismiss",
  /**
   * The campaign-lock banner, verbatim from the prototype. 'paused' means nothing about these
   * contacts can be managed until the campaign is unpaused; 'closed' (completed, or past expiry)
   * means view-only.
   */
  lock: {
    paused: "This campaign is paused. Unpause it to manage its contacts.",
    closed: "This campaign has ended — contacts can be viewed, but not edited, resent, or removed.",
  },
  /**
   * Three distinct nothing-to-show cases. "This campaign has no contacts" and "your filter matched
   * nothing" are different problems with different fixes, so they never share a sentence.
   */
  empty: {
    noContacts: "No contacts on this campaign.",
    noMatches: "No contacts match your search.",
    noneWithStatus: (status: string) => `No ${status.toLowerCase()} contacts.`,
  },
  /** aria-label of the loading region, for a screen reader that can't see the skeleton rows. */
  loadingLabel: "Loading contacts",
  /** Shown when the caller reports a failure but supplies no message of its own. */
  errorFallback: "Couldn't load the contacts for this campaign.",
  /**
   * Truncation note, for the case where the caller's `total` (the campaign's true recipient count)
   * exceeds the number of rows it was able to hand over. The Send Queue endpoint no longer caps
   * its response, so this is now unreachable from that screen and stays only for a future caller
   * that paginates — said plainly under the table so nobody reads a partial list as the whole
   * campaign.
   */
  showingOf: (shown: number, total: number) => `Showing ${shown} of ${total} contacts`,
  /**
   * The three per-row controls, verbatim from `renderContactsTable`. Each has a `title` (the
   * hover hint the prototype shows) and an `aria-label` that NAMES THE RECIPIENT: the icons carry
   * no text, and a screen reader reading three hundred identical "Resend gift email" buttons
   * could not tell which row it is on.
   */
  actions: {
    resend: "Resend gift email",
    resendLabel: (name: string) => `Resend gift email to ${name}`,
    preview: "Preview email",
    previewLabel: (name: string) => `Preview email for ${name}`,
    remove: (name: string) => `Remove ${name}`,
    /** aria-label of the inline-editable EMAIL field (prototype `.cmp-email-input`). */
    emailLabel: (name: string) => `Email for ${name}`,
    /** aria-labels of the inline-editable first/last name fields. */
    firstNameLabel: (name: string) => `First name for ${name}`,
    lastNameLabel: (name: string) => `Last name for ${name}`,
    sendDateLabel: (name: string) => `Send date for ${name}`,
  },
  /** Hover hints on the name and email cells; a campaign lock wins over the status (see `contactFieldHint`). */
  fieldHint: {
    name: {
      pending: "Edit this name before the campaign sends",
      bounced: "Correct this name, then resend",
      locked: "Names can only be edited before the gift email is sent, or after it bounced",
    },
    email: {
      pending: "Edit this address before the campaign sends",
      bounced: "Correct this address, then resend",
      redeemed: (name: string) =>
        `${name} has already redeemed their gift — this address can no longer be changed`,
      sent: "This gift link has already been sent to this address — only bounced addresses can be corrected",
    },
  },
  /**
   * Hover hints on a control that is refused, replacing its normal `title` — the prototype's
   * `resendTitle` / `removeTitle` ladder. Same order of precedence as the toasts below: the
   * campaign lock first, then the contact's own state.
   */
  actionBlocked: {
    paused: "Unpause the campaign to manage contacts",
    closed: "This campaign has ended",
    /** Names every state in `RESENDABLE_STATUSES`, Sent and Opened included. */
    notResendable: "Resend is only available for sent, delivered, opened or bounced contacts",
    coolingDown: (hours: number) => `Resend available in ${hours}h`,
    removeRedeemed: GIFT_REDEEMED,
    removeEnded: "This gift can no longer be removed",
  },
  /**
   * What a refused (or successful) action says, verbatim from the prototype's `showToast` calls in
   * `cmpResendContact` and `removeContact`. Every refusal has its own sentence: "the campaign is
   * paused", "this contact can't be resent to" and "you resent an hour ago" are three different
   * problems with three different fixes.
   */
  toast: {
    resendPaused: "Unpause this campaign to manage its contacts",
    resendClosed: "This campaign has ended — you can't resend its gifts",
    resendNotResendable: "You can only resend to sent, delivered, opened or bounced contacts",
    resendCoolingDown: (hours: number) => `Already resent — try again in ${hours}h`,
    /** The address in the row's input failed `isValidContactEmail` — nothing was sent. */
    invalidEmail: INVALID_EMAIL,
    nameRequired: NAME_REQUIRED,
    removeNotRemovable: (name: string) =>
      `${name}'s gift can no longer be removed from this campaign.`,
    resendSuccess: (email: string) => `Gift email resent to ${email}`,
    removeSuccess: (name: string) => `${name} was removed from this campaign`,
    /** After an inline save lands; the send-date wording is the prototype's `showToast`. */
    nameUpdated: (name: string) => `Name updated for ${name}`,
    emailUpdated: (name: string) => `Email updated for ${name}`,
    sendDateUpdated: (name: string) => `Send date updated for ${name}`,
    /** An inline save whose body carries none of the three fields above. */
    contactUpdated: (name: string) => `Contact updated for ${name}`,
    /**
     * The prototype never blocks the preview, so it has no refusal of its own; a locked campaign
     * makes the WHOLE actions column inoperable here, so these two say why in the prototype's own
     * voice — the paused one is its sentence verbatim, the closed one is shaped like its resend
     * twin.
     */
    previewPaused: "Unpause this campaign to manage its contacts",
    previewClosed: "This campaign has ended — you can't preview its gift emails",
    removePaused: "Unpause this campaign to manage its contacts",
    removeClosed: "This campaign has ended — its contacts can't be removed",
    /**
     * The prototype puts this in a "Can't remove this contact" dialog with no confirm button; it
     * is a refusal with nothing to decide, so it is said in the same toast as every other refusal
     * rather than in a modal on top of a modal.
     */
    removeRedeemed: (name: string) =>
      `${name} has already redeemed their gift, so they can no longer be removed from this campaign.`,
  },
  /*
   * GFG-364 v2 — Add Recipients / Remove / Budget history. Every money argument below is an
   * already-formatted string (`formatUsd`, "$25.00"): the copy never prefixes a currency sign.
   */
  /** Badge on a row added this session. */
  newTag: "New",
  budgetStrip: {
    budgeted: (n: number) => `${n.toLocaleString()} ${n === 1 ? "gift" : "gifts"} budgeted`,
    free: (n: number) => `${n.toLocaleString()} ${n === 1 ? "space" : "spaces"} free`,
    perGift: (price: string) => `${price} per gift`,
    /** Between the three figures: "4 gifts budgeted · 1 space free · $30.00 per gift". */
    separator: " · ",
    history: "Budget history",
  },
  addMenu: {
    button: "Add recipients",
    one: "Add one recipient",
    oneSub: {
      perRecipient: (price: string) => `${price} per recipient`,
      withFree: (n: number, price: string) =>
        `${n.toLocaleString()} ${n === 1 ? "space" : "spaces"} free, then ${price} each`,
      freeOnly: (n: number) => `${n.toLocaleString()} ${n === 1 ? "space" : "spaces"} free`,
      draft: "Draft — no charge",
    },
    bulk: "Bulk upload",
    bulkSub: "Upload a spreadsheet of recipients",
  },
  recipientForm: {
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email",
    country: "Country",
    senderFirstName: "Sender First Name",
    senderLastName: "Sender Last Name",
    senderCompany: "Sender Company",
    sendDate: "Send date",
    recipientSection: "Recipient",
    senderSection: "Sender — how the gift is signed",
    errors: {
      firstName: NAME_REQUIRED,
      email: INVALID_EMAIL,
      country: "Choose the recipient's country",
      duplicate: "This email address is already a recipient of this campaign.",
      sendDate: "Pick a send date after today.",
      sendDateAfterClose: "Pick a send date before the campaign closes.",
    },
  },
  addWizard: {
    detailsTitle: "Add a recipient",
    detailsSubStep: "Step 1 of 2 · Recipient details",
    detailsSub: (campaign: string, n: number) =>
      `${campaign} · ${n.toLocaleString()} ${n === 1 ? "gift" : "gifts"} budgeted`,
    detailsSubDraft: (campaign: string) => `${campaign} · draft, no budget set yet`,
    costPaid: (n: number, total: string) =>
      `Adds ${n} ${n === 1 ? "gift" : "gifts"} to your budget · ${total}`,
    costFree: (n: number, free: number) =>
      `Uses ${n} of ${free} free ${free === 1 ? "space" : "spaces"} — no charge`,
    paymentTitle: (n: number, campaign: string) =>
      `Add ${n} ${n === 1 ? "recipient" : "recipients"} to ${campaign}`,
    paymentSub: { twoStep: "Step 1 of 2 · Payment details", oneStep: "Payment details" },
    sendTitle: (n: number) => `When should we send the gift ${n === 1 ? "email" : "emails"}?`,
    sendSub: "Step 2 of 2 · Sending time",
    lines: {
      freeSpaces: "Free spaces used",
      newGifts: "New gifts",
      budget: "Campaign budget",
      depositHeld: (percent: number) => `${percent}% deposit held`,
      depositTopUp: "Deposit top-up needed",
      walletCredits: "Credits from your wallet",
      invoiced: "Invoiced",
      creditsNeeded: "Credits still needed",
    },
    /** The wallet is short: buy credits in the Buy Credits modal, or the way out when that cannot help. */
    credits: {
      /** "Credits still needed" line value. */
      needed: (credits: string, usd: string) => `${credits} credits (${usd})`,
      buy: "Buy credits",
      /** The Buy Credits modal's no-card message here: its default points at the Wallet header. */
      noCard: "This wallet has no payment card. Add one in the Wallet, then buy credits here.",
      askAdmin: (credits: string, usd: string) =>
        `Your wallet needs ${credits} more credits (${usd}) for these recipients. Ask an account admin to add credits in the Wallet, then try again.`,
      pending: "Your payment is processing. This updates once the credits arrive.",
      checkAgain: "Check again",
      lotExpiry: (purchaseExpiry: string, campaignClose: string) =>
        `Your wallet doesn't have enough credits for these recipients, and buying more won't help: credits bought today expire on ${purchaseExpiry}, but this campaign runs until ${campaignClose}. Credits must last until the campaign ends.`,
      lotExpiryCanAdd: (n: number) =>
        `You can add up to ${n.toLocaleString()} ${n === 1 ? "recipient" : "recipients"} without buying credits.`,
      lotExpiryNoneAffordable: "Contact support to change this campaign's dates.",
    },
    depositModes: {
      title: "Pay the deposit top-up with",
      card: "Card",
      cardSub: "Buy the missing credits now with your wallet's card",
      invoice: "Invoice",
      invoiceSub: "Sent to your billing contact on your agreed terms",
    },
    walletSection: "Funding wallet",
    walletNote: (available: string) =>
      `Funded the original campaign, so it funds these additions too · ${available} available`,
    creditLimit: (more: string, limit: string, headroom: string) =>
      `These recipients need ${more} more exposure, but your credit limit is ${limit} and only ${headroom} is left. Add fewer recipients or contact us to raise the limit.`,
    recap: {
      credits: (amount: string) => `${amount} from your wallet's credits`,
      invoiced: (amount: string) => `${amount} invoiced`,
      free: "No charge — these fit in spaces already paid for",
      detail: (before: string, after: string, verb: string) =>
        `Campaign budget ${before} → ${after} · ${verb} when you confirm below`,
    },
    change: "Change",
    sendOptions: {
      now: "Send now",
      nowSub: "As soon as they're added",
      withCampaign: "With the campaign",
      later: "Schedule for later",
      heading: "Gift email",
      laterSubExpiry: (date: string) => `Any time before ${date}`,
      laterSub: "Pick a date and time",
      date: "Date",
      time: "Time",
      batchDate: "Send date for these recipients",
      /** The zone the picked date and time are read in — the campaign's (`scheduleZoneLabel`). */
      zoneNote: (zone: string) => `Times are in ${zone}`,
    },
    sendErrors: {
      required: "Pick when the gift email should send.",
      past: "Pick a time in the future.",
      pastScheduled: (when: string) => `Pick a time after the campaign starts (${when}).`,
      afterExpiry: (date: string) => `Pick a time before the campaign closes (${date}).`,
    },
    actions: {
      cancel: "Cancel",
      back: "Back",
      continue: "Continue",
      continueToPayment: "Continue to payment",
      addOne: "Add recipient",
      addMany: (n: number) => `Add ${n} recipients`,
      processing: "Processing…",
    },
    toastAdded: (n: number, campaign: string, budget: string | null) =>
      `${n} ${n === 1 ? "recipient" : "recipients"} added to ${campaign}${
        budget ? ` · budget now ${budget}` : ""
      }`,
    quoteError: "Couldn't price these recipients. Try again.",
  },
  /** The row × on Remove: the confirmation copy (the modal's title and buttons are `adminCampaigns`' defaults). */
  remove: {
    confirmParagraphs: [
      "If you remove this contact from the campaign, their redemption link will no longer be valid and they will not be able to claim their gift, even though they may have received an email from you telling them they have a gift.",
      "Removed contacts do not trigger a refund on your gift budget. We recommend adding new contacts to replace any that you remove so you can take advantage of the campaign’s full gift budget.",
      "Are you sure you want to remove this contact?",
    ],
    success: "Contact removed — they won't get a gift.",
    blocked: {
      redeemed: (name: string) => `${name} has already redeemed their gift`,
      last: "A campaign needs at least one recipient. To stop this campaign, delete or archive it instead.",
    },
  },
  budgetHistory: {
    title: "Budget history",
    subtitle: (campaign: string, n: number) =>
      `${campaign} · ${n.toLocaleString()} ${n === 1 ? "amendment" : "amendments"}`,
    empty: "No amendments yet.",
    payment: {
      invoice: (ref: string) => `Invoice ${ref}`,
      purchase: (ref: string) => `Card purchase ${ref}`,
    },
    columns: {
      date: "Date",
      who: "Who",
      budget: "Budget",
      deposit: "Deposit",
      recipients: "Recipients",
      credits: "Credits",
      payment: "Payment",
      expiry: "Expiry",
    },
  },
  bulk: {
    skipped: (n: number) =>
      `${n.toLocaleString()} ${n === 1 ? "recipient was" : "recipients were"} skipped: already on this campaign or repeated in the file.`,
    invalidRows: (n: number) =>
      n === 1
        ? "1 row needs fixing before it can be added:"
        : `${n.toLocaleString()} rows need fixing before they can be added:`,
    rowError: (row: number, email: string, message: string) =>
      email.trim()
        ? `Row ${row} (${email}): ${message.replace(/\.$/, "")}`
        : `Row ${row}: ${message.replace(/\.$/, "")}`,
    sendDateFormat: "Use a real YYYY-MM-DD send date",
    reupload: "Fix the spreadsheet and upload it again.",
  },
} as const;

/**
 * `campaign-spring-appeal-redeemed-contacts.csv` — the prototype's `exportContactsCsv` filename
 * shape (stem, optional status suffix, `-contacts.csv`), with a slug of the campaign name in place
 * of its numeric id, which this modal is deliberately not given.
 */
export function campaignContactsCsvFilename(campaignName: string, status: string | null): string {
  const slug =
    campaignName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || CSV_FALLBACK_SLUG;
  const suffix = status ? `-${status.toLowerCase()}` : "";
  return `campaign-${slug}${suffix}-contacts.csv`;
}

/** Prototype `.cmp-email-input` — invisible until hovered, a real field once focused. No width floor. */
const INLINE_INPUT_LOOK_CLASS =
  "rounded-[7px] border border-transparent bg-transparent px-2 py-1.5 " +
  "text-[14px] text-rodeo-black hover:border-smoke focus:border-dark-dust focus:bg-white " +
  "focus:outline-none read-only:hover:border-transparent";

/** The inline Send Date picker: a 180px floor, the room its date and calendar icon need. */
export const INLINE_INPUT_CLASS = `w-full min-w-[180px] ${INLINE_INPUT_LOOK_CLASS}`;

/**
 * The inline name and email fields: a 110px floor, so the contacts table fits a ~1170px viewport
 * without scrolling sideways, even with a NEW tag beside a first name.
 */
export const INLINE_TEXT_INPUT_CLASS = `w-full min-w-[110px] ${INLINE_INPUT_LOOK_CLASS}`;
